import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  PlusIcon, 
  XMarkIcon, 
  UserIcon,
  CalendarIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { SeasonService, ContestantService } from '../../../services/firebaseService';

const schema = yup.object({
  name: yup.string().required('Season name is required').min(3, 'Season name must be at least 3 characters'),
  startDate: yup.date().required('Start date is required'),
  endDate: yup.date().required('End date is required').min(yup.ref('startDate'), 'End date must be after start date'),
  description: yup.string().optional(),
}).required();

export default function CreateSeason() {
  const [contestants, setContestants] = useState([]);
  const [selectedContestants, setSelectedContestants] = useState([]);
  const [availableContestants, setAvailableContestants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showContestantModal, setShowContestantModal] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    loadContestants();
  }, []);

  const loadContestants = async () => {
    try {
      const allContestants = await ContestantService.getAllContestants();
      setAvailableContestants(allContestants);
    } catch (error) {
      console.error('Error loading contestants:', error);
    }
  };

  const onSubmit = async (data) => {
    if (selectedContestants.length === 0) {
      setError('Please select at least one contestant for the season.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const seasonData = {
        ...data,
        status: 'active',
        contestants: selectedContestants.map(c => c.id),
        weeks: [],
        createdAt: new Date(),
      };

      const seasonId = await SeasonService.createSeason(seasonData);
      
      // Update contestants to include this season
      for (const contestant of selectedContestants) {
        const updatedSeasons = [...(contestant.seasons || []), seasonId];
        await ContestantService.updateContestant(contestant.id, { seasons: updatedSeasons });
      }

      navigate(`/admin/seasons/${seasonId}`);
    } catch (error) {
      console.error('Error creating season:', error);
      setError('Failed to create season. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const addContestant = (contestant) => {
    if (!selectedContestants.find(c => c.id === contestant.id)) {
      setSelectedContestants([...selectedContestants, contestant]);
    }
    setShowContestantModal(false);
  };

  const removeContestant = (contestantId) => {
    setSelectedContestants(selectedContestants.filter(c => c.id !== contestantId));
  };

  const createNewContestant = async (contestantData) => {
    try {
      const contestantId = await ContestantService.createContestant(contestantData);
      const newContestant = { id: contestantId, ...contestantData };
      setAvailableContestants([...availableContestants, newContestant]);
      addContestant(newContestant);
    } catch (error) {
      console.error('Error creating contestant:', error);
      setError('Failed to create contestant. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create New Season</h1>
              <p className="mt-1 text-sm text-gray-500">
                Set up a new Survivor season for fantasy leagues.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Season Details */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Season Information</h2>
            
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Season Name
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name')}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                  placeholder="e.g., Survivor 50: Philippines"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description (Optional)
                </label>
                <input
                  type="text"
                  id="description"
                  {...register('description')}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                  placeholder="Brief description of the season"
                />
              </div>

              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                  Start Date
                </label>
                <input
                  type="date"
                  id="startDate"
                  {...register('startDate')}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                />
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                  End Date
                </label>
                <input
                  type="date"
                  id="endDate"
                  {...register('endDate')}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                />
                {errors.endDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.endDate.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contestants Selection */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Contestants</h2>
              <button
                type="button"
                onClick={() => setShowContestantModal(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Contestant
              </button>
            </div>

            {selectedContestants.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {selectedContestants.map((contestant) => (
                  <div
                    key={contestant.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center">
                      <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">{contestant.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeContestant(contestant.id)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No contestants selected</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Add contestants to this season to get started.
                </p>
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/admin/seasons')}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Create Season
                </>
              )}
            </button>
          </div>
        </form>

        {/* Contestant Selection Modal */}
        {showContestantModal && (
          <ContestantSelectionModal
            availableContestants={availableContestants}
            selectedContestants={selectedContestants}
            onAddContestant={addContestant}
            onCreateContestant={createNewContestant}
            onClose={() => setShowContestantModal(false)}
          />
        )}
      </div>
    </div>
  );
}

// Contestant Selection Modal Component
function ContestantSelectionModal({ 
  availableContestants, 
  selectedContestants, 
  onAddContestant, 
  onCreateContestant, 
  onClose 
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newContestant, setNewContestant] = useState({ name: '', bio: '' });

  const filteredContestants = availableContestants.filter(contestant =>
    contestant.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !selectedContestants.find(c => c.id === contestant.id)
  );

  const handleCreateContestant = (e) => {
    e.preventDefault();
    if (newContestant.name.trim()) {
      onCreateContestant(newContestant);
      setNewContestant({ name: '', bio: '' });
      setShowCreateForm(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Add Contestant</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {!showCreateForm ? (
            <>
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search contestants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                />
              </div>

              <div className="max-h-60 overflow-y-auto">
                {filteredContestants.length > 0 ? (
                  <div className="space-y-2">
                    {filteredContestants.map((contestant) => (
                      <button
                        key={contestant.id}
                        onClick={() => onAddContestant(contestant)}
                        className="w-full text-left p-2 hover:bg-gray-100 rounded-md flex items-center"
                      >
                        <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm">{contestant.name}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    {searchTerm ? 'No contestants found matching your search.' : 'No available contestants.'}
                  </p>
                )}
              </div>

              <div className="mt-4 pt-4 border-t">
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create New Contestant
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={handleCreateContestant}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={newContestant.name}
                    onChange={(e) => setNewContestant({ ...newContestant, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                    placeholder="Contestant name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Bio (Optional)</label>
                  <textarea
                    value={newContestant.bio}
                    onChange={(e) => setNewContestant({ ...newContestant, bio: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                    placeholder="Brief biography"
                    rows={3}
                  />
                </div>
              </div>
              <div className="mt-4 flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
                >
                  Create
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
